import { useContext } from "react";
import { ITopLeagueFeedResponse } from "@finbackoffice/websocket-client";
import { useParams } from "next/navigation";
import { Params } from "next/dist/server/request/params";
import { FeedRidContext, PrematchFeedContext } from "@finbackoffice/site-core";
import { useSocketStorage } from "hooks/base";

export type ISelectedPrematchParams = {
    sportSlug: string;
    categoryId: number;
    tournamentId: number;
};

function getDefaultParamsFromTopLeagues(
    topLeagues: ITopLeagueFeedResponse[],
): ISelectedPrematchParams | null {
    if (topLeagues.length === 0) {
        return null;
    }

    const [firstTopLeague] = topLeagues;

    return {
        sportSlug: firstTopLeague.sport_slug,
        categoryId: firstTopLeague.category_id,
        tournamentId: firstTopLeague.id,
    };
}

function getDefaultPrematchParamsFromUrl(params: Params): ISelectedPrematchParams {
    const [prematchGameRoutesSport, prematchGameRoutesCategoryId, prematchGameRoutesTournamentId] =
        params.prematchGameRoutes ?? [];
    const sport = params.sport || prematchGameRoutesSport;
    const categoryId = params.categoryId || prematchGameRoutesCategoryId;
    const tournamentId = params.tournamentId || prematchGameRoutesTournamentId;

    return {
        sportSlug: sport as string,
        categoryId: parseInt(categoryId as string),
        tournamentId: parseInt(tournamentId as string),
    };
}

export const useDefaultPrematchParamsFromUrl = (): ISelectedPrematchParams => {
    const params = useParams();
    return getDefaultPrematchParamsFromUrl(params);
};

export const useMultiSelectPrematchParams = (): ISelectedPrematchParams | null => {
    const { prematchTopLeagues, hasPrematchSports } = useContext(PrematchFeedContext);
    const params = useParams();
    const { sportSlug, categoryId, tournamentId } = getDefaultPrematchParamsFromUrl(params);
    const defaultTopLeaguesParams = getDefaultParamsFromTopLeagues(prematchTopLeagues);
    const { prematchRid } = useContext(FeedRidContext);
    const { getSports, getCategoryById, getTournamentById, getTournaments } =
        useSocketStorage(prematchRid);
    const defaultUrlParams = { sportSlug, categoryId, tournamentId };

    if (!hasPrematchSports) {
        return null;
    }

    if (!sportSlug || !categoryId || !tournamentId) {
        return defaultTopLeaguesParams;
    }

    const sportFromUrl = getSports(prematchRid).find((sport) => sport.slug === sportSlug);

    const categoryFromUrl = getCategoryById(categoryId);

    if (!sportFromUrl || !categoryFromUrl) {
        return defaultTopLeaguesParams;
    }

    const tournamentFromUrl = getTournamentById(tournamentId);

    if (!tournamentFromUrl) {
        const [firstTournament] = getTournaments(categoryId) ?? [];

        if (!firstTournament) {
            return defaultUrlParams;
        }

        return {
            sportSlug,
            categoryId,
            tournamentId: firstTournament.id,
        };
    }

    return defaultUrlParams;
};

export const useMultiSelectPrematchCurrentTournamentId = (): number | null => {
    const prematchParams = useMultiSelectPrematchParams();

    if (!prematchParams) {
        return null;
    }

    return prematchParams.tournamentId;
};
